import { useState } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  IconButton, 
  Drawer, 
  Box, 
  List, 
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import BurgerMenu from './BurgerMenu';
import MapIcon from '@mui/icons-material/Map';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import { useReset } from '../../context/ResetContext';

interface LayoutProps {
  children: React.ReactNode;
  onReset: () => void;
  onOpenAbout: () => void;
}

const Layout = ({ children, onReset, onOpenAbout }: LayoutProps) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  // We'll use default MUI breakpoints instead of explicit checks
  const { triggerReset } = useReset();

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleReset = () => {
    onReset();
    triggerReset();
    setDrawerOpen(false);
  };

  const handleSupportClick = () => {
    window.open("https://www.paypal.com/paypalme/crono972", "paypalFhocs");
    setDrawerOpen(false);
  };

  const handleAboutClick = () => {
    onOpenAbout();
    setDrawerOpen(false);
  };

  const drawer = (
    <Box sx={{ width: 250, p: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <IconButton onClick={handleDrawerToggle}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Typography variant="h6" sx={{ p: 2, textAlign: 'center' }}>
        Es ou konet Péyi nou
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <List>
        <ListItemButton onClick={handleReset}>
          <ListItemIcon>
            <RestartAltIcon />
          </ListItemIcon>
          <ListItemText primary="Recommencer" />
        </ListItemButton>
        <ListItemButton onClick={handleSupportClick}>
          <ListItemIcon>
            <VolunteerActivismIcon />
          </ListItemIcon>
          <ListItemText primary="Soutenir le projet" />
        </ListItemButton>
        <ListItemButton onClick={handleAboutClick}>
          <ListItemIcon>
            <InfoIcon />
          </ListItemIcon>
          <ListItemText primary="A propos" />
        </ListItemButton>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar 
        position="fixed" 
        elevation={1}
        sx={{
          background: 'linear-gradient(90deg, #e41e2b 0%, #00843d 50%, #000000 100%)',
          color: '#ffffff'
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { xs: 'flex', md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <MapIcon sx={{ mr: 1 }} />
            <Typography variant="h6" sx={{ 
              textShadow: '1px 1px 3px rgba(0,0,0,0.5)',
              fontWeight: 'bold'
            }}>
              Komin Matnik
            </Typography>
          </Box>
          {/* Only show the burger menu button on medium-large screens */}
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <BurgerMenu onReset={onReset} />
          </Box>
        </Toolbar>
      </AppBar>
      
      <Drawer
        variant="temporary"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': { 
            width: { xs: '80%', sm: 250 },
            boxSizing: 'border-box',
            boxShadow: 3
          },
        }}
      >
        {drawer}
      </Drawer>
      
      <Box 
        component="main" 
        sx={{ 
          flexGrow: 1, 
          width: '100%',
          mt: { xs: '56px', sm: '64px' } // Adjust based on AppBar height
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
